import React from 'react';
import { Tr, Td} from '@patternfly/react-table';
import { Tooltip } from '@patternfly/react-core';
import EditableBranchCell from './EditableBranchCell';
import EditableImageCell from './EditableImageCell';

import { ReviewRowProps } from './types';

const ReviewRow: React.FC<ReviewRowProps> = ({
  application,
  branchMapping,
  applicationBranch,
  imageValidation,
  deployTag,
  dummyImages,
  setBranchMapping,
  setDeployTagMapping,
  setImageValidationMap,
  branchValidation,
  isTopLevel,
  exists,
  actualBranchUsed,
  repoNameMap,
  isInfrastructure,
  refreshing,
  refreshBulkResultForApp,
  repository,
  defaultBranch,
  latestImages,
  requiredLabelsOk
}) => {
  let statusDisplay: React.ReactNode = null;

  if (!exists) {
    statusDisplay = 
    <Tooltip content="This feature branch wasn’t found in app repo, so we’ve automatically switched you to the default branch">
      <span>
        <span style={{ color: 'blue' }}>fall back to default</span>
      </span>
    </Tooltip>
  } else if (branchValidation && branchValidation.behind_by > 0) {
    statusDisplay = (
      <span style={{ color: 'red' }}>
        Behind by {branchValidation.behind_by} commits
      </span>
    );
  } else {
    statusDisplay = <span style={{ color: 'green' }}>Up-to-date</span>;
  }

  const branchValue = branchMapping[application]  ?? actualBranchUsed ?? applicationBranch;

  return (
    <Tr key={application}>
      <Td dataLabel="Apps/Services to Deploy">{application}</Td>
      <Td dataLabel="Feature Branch">
        <EditableBranchCell
          application={application}
          repoName={repoNameMap[application]}
          branchValue={branchValue}
          onChange={(newValue: string) => {
            setBranchMapping((prev) => ({ ...prev, [application]: newValue }));
            refreshBulkResultForApp(application, newValue);
          }}
        />
      </Td>
      <Td dataLabel="Branch Status">
        {refreshing
            ? <div className="dx-spinner dx-spinner--sm" aria-label="Refreshing branch status" />
            : statusDisplay}
      </Td>
       {/* ─────────────── “Update” column ─────────────── */}
      <Td dataLabel="Update">
        {branchValidation && branchValidation.behind_by > 0 ? (
          <a
            href={`https://github.com/${repository}/compare/${branchValue}...${defaultBranch}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Update
          </a>
        ) : (
          <span style={{ color: 'var(--text-muted)' }}>—</span>
        )}
      </Td>
      <Td dataLabel="Required Labels">
        {requiredLabelsOk
          ? <span style={{ color: 'green' }}>✓</span>
          : <span style={{ color: 'var(--accent-amber)' }}>No label detected</span>}
      </Td>
      <Td dataLabel="Image Validation">
        {refreshing ? (
          <div className="dx-spinner dx-spinner--sm" aria-label="Refreshing image validation" />
        ) : imageValidation === 'not found' ? (
          <span style={{ color: 'red' }}>not found</span>
        ) : (
          'found'
        )}
      </Td>
      {isInfrastructure ? (
        <Td dataLabel="Deploy Tag">{branchValue}</Td>
      ) : (
        <Td dataLabel="Deploy Tag">
          <EditableImageCell
            imageValue={deployTag}
            onChange={(newValue: string) => {
              const cleaned = newValue
                .replace(/\s*\(.+?\)\s*$/, '') // drop timestamp/annotations
                .replace(/^commit-/, '');      // keep 10-char sha only
              setDeployTagMapping(prev => ({ ...prev, [application]: cleaned }));
            }}
            dummyImages={dummyImages}
            disabled={!!latestImages}     // disable when no foundTags
          />
        </Td>
      )}
      {latestImages !== undefined && (
       <Td dataLabel="Latest Images">
         {latestImages.length > 0 ? (
           <EditableImageCell
              imageValue={ latestImages 
                ? ('Select latest image to deploy') 
                : deployTag
              }
              placeholder="Select latest image to deploy"
              // format your dropdown options exactly how the user will pick them
              dummyImages={latestImages}
              onChange={value => {
                const clean = value.split(' ')[0].replace(/^commit-/, '');
                setDeployTagMapping(prev => ({ ...prev, [application]: clean }))
             }}
           />
         ) : (
           <span style={{ fontStyle: 'italic' }}>
             No images are found in artifactory
           </span>
         )}
       </Td>
     )}
    </Tr>
  );
};

export default ReviewRow;
